<script lang="ts">
  import { fly } from "svelte/transition";

  import Info from "../Reusables/Info.svelte";
  import Grid from "../Reusables/Grid.svelte";
  import GridCell from "../Reusables/GridCell.svelte";
  import CleanLayout from "../Reusables/CleanLayout.svelte";
  import CleanTopper from "../Reusables/CleanTopper.svelte";
  import CleanFooter from "../Reusables/CleanFooter.svelte";
  import CleanOverlay from "../Reusables/CleanOverlay.svelte";
  import CleanChat from "../Reusables/CleanChat.svelte";
  import type { Connect4State, Player, User } from "../../../server/interfaces";
  import { WebSocket, in_game, show_chat, Utils } from "../stores";
  import { fix_state } from "../helpers";

  let game: Connect4State;
  let self: Player;
  let self_profile: User;
  let opponent: Player;
  let opponent_profile: User;

  let messages: Array<{
    user: User;
    content: string;
    time: number;
    spectator: boolean;
  }> = [];

  let new_message = false;

  $WebSocket.on("OnMessage", (msg) => {
    if (
      messages.length == 0 ||
      messages[messages.length - 1].time != msg.time
    ) {
      messages = messages.concat([msg]);
      new_message = !$show_chat;
    }
  });

  let player_count = 0;
  Utils.GameStats.subscribe((e) => {
    if (e) {
      player_count = e.total_players;
    }
  });

  $WebSocket.on("OnGameUpdate", (game_state) => {
    let gs = <Connect4State>game_state;
    game = gs;
    self = gs.state === "Spectator" ? gs.player1 : gs.self;
    if (gs.player2)
      opponent = gs.player2.id === self.id ? gs.player1 : gs.player2;
    if (self) self_profile = self.user;
    if (opponent) opponent_profile = opponent.user;
    in_game.set(true);
  });

  function leave_game() {
    $WebSocket.emit("LeaveRoom");
    window.history.replaceState({}, document.title, window.location.pathname);
    in_game.set(false);
  }

  function on_create_room(name: string): Promise<string> {
    return new Promise((resolve, reject) => {
      $WebSocket.emit("CreateRoom", name, "Connect4", (room_id) => {
        resolve(room_id);
      });
    });
  }

  function on_join_room(room: string, name: string): Promise<string> {
    return new Promise((resolve, reject) => {
      $WebSocket.emit("JoinRoom", room, name, (room_id) => {
        resolve(room_id);
      });
    });
  }
</script>

<div class="game">
  {#if !$in_game}
    <Info GameName="Connect 4" {player_count} {on_create_room} {on_join_room} />
  {:else}
    <CleanLayout GameName="Connect 4" show_chat={$show_chat}>
      <CleanTopper
        slot="topper"
        {leave_game}
        room_id={game.room_id}
        spectating={game.state === "Spectator"}
        spectators={game.spectator_count}
      >
        <i
          class={`fas fa-comment${new_message ? "-dots" : ""} ctog`}
          on:click={() => {
            show_chat.set(!$show_chat);
            new_message = false;
          }}
        />
      </CleanTopper>

      <CleanChat
        slot="chat"
        w={340}
        h={360}
        m={10}
        {messages}
        send_func={(content) => {
          $WebSocket.emit("SendMessage", content);
        }}
      />

      <CleanOverlay
        slot="overlay"
        title={fix_state(self.state)}
        wins_2={opponent ? opponent.wins : 0}
        wins_1={self ? self.wins : 0}
        {leave_game}
        rematch={() => $WebSocket.emit("Rematch")}
        show={self.state !== "ongoing"}
        show_history={self.state !== "waiting"}
        show_actions={!(game.state === "Spectator")}
        show_rematch={self.state !== "forfeit"}
      />

      <Grid slot="game" x={7} y={6}>
        {#each game.board as r, _row}
          {#each r as emoji, column}
            <GridCell
              on_click={() => $WebSocket.emit("Turn", column)}
              clickable={!emoji}
              highlight={false}
            >
              {#if emoji}
                <div
                  in:fly={{ y: -140, duration: 400 }}
                  class={`circle e${emoji}`}
                />
              {/if}
            </GridCell>
          {/each}
        {/each}
      </Grid>

      <CleanFooter
        slot="footer"
        player_2={!opponent ? "invite a friend" : opponent_profile.name}
        player_1={self_profile.name}
        indicate_2={opponent && opponent.turn && self.state === "ongoing"
          ? "Opponent's Turn"
          : opponent && opponent.rematch
          ? "Wants to rematch"
          : ""}
        indicate_1={self.turn && self.state === "ongoing"
          ? `${game.state === "Spectator" ? "Opponents" : "Your"} Turn`
          : self.rematch
          ? "Wants to rematch"
          : ""}
      >
        <div
          slot="icon_1"
          class={`circle e${self.piece}`}
          style="width: 25px; height: 25px"
        />
        <div
          slot="icon_2"
          class={`circle e${opponent ? opponent.piece : 2}`}
          style="width: 25px; height: 25px"
        />
      </CleanFooter>
    </CleanLayout>
  {/if}
</div>

<style>
  .game {
    overflow: hidden;
  }

  .fa-comment-dots {
    animation: float 800ms ease-in-out;
  }
</style>
